import { flash, Modal, Tooltip } from "@xola/ui-kit";
import React from "react";
import { Guide, User, UserShortCode } from "types";
import { xolaUri } from "../../api/xolaUri";
import { useSeller } from "../../hooks/useAccount";
import { useAutoFetchCollection } from "../../hooks/useAutoFetchCollection";
import { useResource } from "../../hooks/useResource";
import { NoTranslate } from "./NoTranslate";

export interface ExternalCalendarLinkModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId?: ID;
}

export const ExternalCalendarLinkModal = ({ isOpen, onClose, userId }: ExternalCalendarLinkModalProps) => {
    return (
        <Modal shouldCloseOnOutsideClick isOpen={isOpen} size="medium" onClose={onClose}>
            <ModalContent userId={userId} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps extends Omit<ExternalCalendarLinkModalProps, "isOpen"> {}

const ModalContent = ({ userId }: ModalContentProps) => {
    const seller = useSeller();
    const { data: user } = useResource<User>(["users", userId]);
    const { data: guides, isLoading } = useAutoFetchCollection<Guide>(["sellers", seller.id, "guides"]);
    const isGuide = guides.some((guide) => guide.user.id === userId) ?? false;
    const shortCodeUserId = isLoading ? null : isGuide ? userId : seller.id;
    const { data: userShortCode } = useResource<UserShortCode>(["users", shortCodeUserId, "code"], null, {
        enabled: !!shortCodeUserId,
    });

    if (isLoading) {
        return null;
    }

    const url = `${xolaUri}/calendar/${userShortCode?.shortCode}${isGuide ? "/guide" : ""}`;
    const handleUrlClick = () => {
        navigator.clipboard.writeText(url);
        flash.success(`${url} copied`);
    };

    return (
        <>
            <Modal.Header>Sync Calendar</Modal.Header>
            <Modal.Body>
                {userShortCode ? (
                    <>
                        <Tooltip content="Click to copy URL">
                            <NoTranslate as="div" className="ui-calendar-url cursor-copy" onClick={handleUrlClick}>
                                {url}
                            </NoTranslate>
                        </Tooltip>
                        <div className="mt-4">
                            Use the above link to sync your Xola calendar with external calendars (like Apple&apos;s
                            Calendar)
                        </div>
                    </>
                ) : (
                    <div>
                        You do not have permission to access the calendar link for{" "}
                        <var data-var="user">{user?.name ?? userId}</var>
                    </div>
                )}
            </Modal.Body>
        </>
    );
};
